import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Row,
  Segmented,
} from "antd";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import dayjs from "dayjs";
import {
  getListNutritionistReassessmentFormApi,
  getNutritionistReassessmentFormApi,
  getPatientDetailsApi,
} from "../../../../services/forms.services";
import { PlusOutlined } from "@ant-design/icons";
import EditIcon from "../../../ui/EditIcon";
import useQuery from "../../../../hooks/useQuery";
import useIsMobile from "../../../../hooks/useIsMobile";
import "../../../../App.css";
import NutritionistReassessmentSummary from "./NutritionistReassessmentSummary";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import ReassessmentTabs from "./ReassessmentTabs";
import assessmentIcon from "../../../../assets/images/nurseIcons/assessment.svg";
import UpcomingReassessmentDate from "../../../IpEMR/Nutritionist/NutritionistInitialInspection/UpcomingReassessmentDate";
import "../../../IpEMR/Doctor/DailyReview/DailyReview.css";

const NutritionistReassessment = ({ patientData, patient, refreshEmr }) => {
  const isMobile = useIsMobile();
  const [activeSegment, setActiveSegment] = useState(null);
  const [pressureSorePoints, setPressureSorePoints] = useState([]);
  const [editable, setEditable] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [details, setDetails] = useState(null);
  const [reviewList, setReviewList] = useState([]);
  const [isNewForm, setIsNewForm] = useState(false);
  const [data, setData] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState(null);
  const isActive = useQuery()?.status === "current";

  const refreshPage = () => setRefresh((prev) => !prev);

  useEffect(() => {
    if (patientData?.admission?.id) {
      getListNutritionistReassessmentFormApi(Number(patientData?.admission?.id))
        .then((res) => {
          setReviewList(() => {
            const temp = res?.data?.data;
            setActiveSegment(temp?.[0]?.id);
            return temp;
          });
        })
        .catch((err) => console.log(err));

      getPatientDetailsApi(patientData?.admission?.id)
        .then((res) => {
          setDetails(res?.data);
        })
        .catch((err) => console.log(err));
    }
  }, [patientData?.admission?.id, refresh]);

  const segmentData = reviewList?.map((item) => ({
    label: `${dayjs(item?.created_at).format("MMM DD, h:mm A")}`,
    value: item?.id,
  }));

  useEffect(() => {
    if (reviewList?.length > 0 && !isNewForm) {
      setEditable(false);
      setActiveSegment(reviewList[0]?.id);
    }
  }, [reviewList?.length, isNewForm]);

  useEffect(() => {
    if (activeSegment) {
      getNutritionistReassessmentFormApi(activeSegment)
        .then((res) => {
          setData(res?.data?.data?.data);
          setFormData(res?.data?.data);
        })
        .catch((error) => console.error(error));
    }
  }, [activeSegment, refresh]);

  const handleChangeSegment = (value) => {
    setActiveSegment(value);
  };

  const handleEdit = () => {
    setEditable(true);
    setOpenModal(true);
  };

  const handleNewForm = () => {
    setIsNewForm(true);
    setPressureSorePoints([]);
    setEditable(true);
    setActiveSegment(null);
    setData({}); // ← clear old form data
    setFormData(null); // ← clear old form metadata
    setOpenModal(true);
  };

  return (
    <>
      {reviewList?.length > 0 ? (
        <Row gutter={[12, 12]} className="daily-review-page">
          <Col span={24}>
            <div className="daily-review-toolbar nutrition-reassessment-toolbar">
              <div className="daily-review-toolbar__left">
                {isActive && (
                  <Button
                    type="primary"
                    onClick={handleNewForm}
                    className={`${btnStyles.lgBtn} inv-btn daily-review-toolbar__add`}
                    style={{ marginRight: "15px", fontWeight: 500 }}
                  >
                    <PlusOutlined /> Reassessment
                  </Button>
                )}
                <div className="daily-review-toolbar__dates">
                  <Segmented
                    className="daily-review-date-segment"
                    value={activeSegment}
                    options={segmentData}
                    onChange={handleChangeSegment}
                    size="middle"
                    style={{
                      boxShadow: "2px 2px 3px #e3e4e6",
                    }}
                  />
                </div>
              </div>

              {isActive && !isMobile && (
                <Button
                  type="primary"
                  onClick={handleEdit}
                  className={`${btnStyles.lgBtn} inv-btn daily-review-toolbar__edit`}
                  disabled={!activeSegment}
                  style={{ fontWeight: 500 }}
                >
                  <EditIcon />
                  Edit
                </Button>
              )}
            </div>
            {isActive && isMobile && (
              <div className="nutrition-reassessment-edit-row">
                <Button
                  type="primary"
                  onClick={handleEdit}
                  className={`${btnStyles.lgBtn} inv-btn`}
                  disabled={!activeSegment}
                  style={{ fontWeight: 500 }}
                >
                  <EditIcon />
                  Edit
                </Button>
              </div>
            )}
          </Col>
          <Col span={24}>
            <UpcomingReassessmentDate
              admissionId={patient}
              patientData={patientData}
              refreshPage={refreshEmr}
            />
          </Col>

          <Col span={24} style={{ textAlign: "center" }}>
            <NutritionistReassessmentSummary data={data} formData={formData} />
          </Col>
        </Row>
      ) : (
        <Row
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
          }}
        >
          <Col span={24} style={{ textAlign: "center" }}>
            <NoDataPage
              title={"Reassessment Not Yet Submitted "}
              subtitle={isActive && "Kindly, upload a reassessment here"}
              image={assessmentIcon}
            />
            {isActive && (
              <Button
                type="primary"
                onClick={handleNewForm}
                className={`${btnStyles.lgBtn} inv-btn `}
                style={{ fontWeight: "normal" }}
              >
                Reassessment Form
              </Button>
            )}
          </Col>
        </Row>
      )}
      {openModal && (
        <ReassessmentTabs
          openModal={openModal}
          setOpenModal={setOpenModal}
          patientData={patientData}
          details={details}
          pressureSorePoints={pressureSorePoints}
          refreshPage={refreshPage}
          reviewList={reviewList}
          data={data}
          formData={formData}
          setPressureSorePoints={setPressureSorePoints}
          isNewForm={isNewForm}
          setActiveSegment={setActiveSegment}
          activeSegment={activeSegment}
        />
      )}
    </>
  );
};

export default NutritionistReassessment;
